import {
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip, SpeedDial } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import mobileApplicationReleaseProfileService from '../../services/mobileApplicationReleaseProfileService';

const MobileApplicationReleaseProfilesPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { canPlatform } = useCheckPermission();
  const canManageBuildProfiles = canPlatform(['ManageBuildProfiles']);
  const canDeleteBuildProfiles = canManageBuildProfiles;
  const canCreateBuildProfiles = canManageBuildProfiles;

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    mobileApplicationReleaseProfileService
      .list()
      .then((response) => {
        if (!response.hasError) {
          setRows(response.records);
        } else {
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [];
    const readonly = !canManageBuildProfiles;

    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="Megtekintés">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Szerkesztés">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'Megtekintés' : 'Szerkesztés'}
        onClick={() =>
          navigate(`/mobileApplicationReleaseProfile/edit/${params.row.id}`)
        }
      />
    );

    if (canDeleteBuildProfiles) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Törlés">
              <DeleteIcon />
            </Tooltip>
          }
          label="Törlés"
          onClick={() =>
            setParams({
              open: true,
              name: 'Biztosan törlöd ezt a kiadási profilt?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Név', flex: 1.2 },
    {
      field: 'tenantName',
      headerName: 'Bérlő',
      flex: 1,
      valueGetter: (_value: any, row: any) =>
        row.tenantName || row.TenantName || '—',
    },
    { field: 'appTitle', headerName: 'App cím', flex: 1 },
    {
      field: 'publicId',
      headerName: 'PublicId (GUID)',
      flex: 1.4,
    },
    {
      field: 'buildApiKeyPrefix',
      headerName: 'API kulcs',
      flex: 0.8,
      valueFormatter: (value: string) => (value ? `${value}••••••••` : ''),
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 0.6,
      align: 'right',
      getActions,
    },
  ];

  const handleDelete = (id: number) => {
    mobileApplicationReleaseProfileService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés!', { variant: 'success' });
        setRows(rows.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{ p: 3 }}>
        <Grid size={12}>
          <h2>Alkalmazás kiadási profil</h2>
        </Grid>
        {canCreateBuildProfiles && (
          <Grid container size={12} sx={{ p: 2, justifyContent: 'end' }}>
            <Grid sx={{ display: { xs: 'none', sm: 'fixed' } }}>
              <Tooltip title="Új kiadási profil">
                <IconButton
                  component={RouterLink}
                  to="/mobileApplicationReleaseProfile/create"
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDeleteDialog />
        <SpeedDial
          onClick={() => navigate('/mobileApplicationReleaseProfile/create')}
          sx={{
            display: { xs: 'fixed', sm: 'none' },
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          icon={<AddIcon />}
          ariaLabel="Hozzáadás"
        />
      </Grid>
    </Paper>
  );
};

export default MobileApplicationReleaseProfilesPage;
